import IntegrationSettingsBlock from './IntegrationSettingsBlock.jsx';
import ZoomIcon from '../icons/ZoomIcon.jsx';
import {
    setZoomQuery,
    useBookableResourcesQuery,
    useZoomQuery,
} from '../../lib/Queries';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import * as Toast from '../../lib/Toast.js';
import { Link } from 'react-router-dom';
import { getBusinessUrl } from '../../lib/useBusinessNavigate.js';
import { useEffect, useState } from 'react';
import Select from '~/components/Select';
import Loading from '~/components/Loading';
import ErrorMessage from '~/components/ErrorMessage';

const ZoomAccountRow = ({ userinfo, error }) => {
    const { save: saveDisconnect, isSaving: isDisconnecting } =
        useBusinessPostApi(`/integrations/other/zoom/disconnect`);

    function disconnect() {
        saveDisconnect({
            id: userinfo.id,
        }).then(result => {
            setZoomQuery(result);
            Toast.success(
                'Successfully disconnected Zoom. Please update the location for any Event Types that are using Zoom.',
            );
        });
    }

    return (
        <div className="py-2 flex items-center justify-between pr-4">
            <div>
                {userinfo.display_name} ({userinfo.email})
                {!!error && <ErrorMessage>{error}</ErrorMessage>}
            </div>

            <button
                type="button"
                className="btn-pink-pill-sm"
                onClick={disconnect}
            >
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
        </div>
    );
};

const ZoomResourceMapping = ({ resources, accounts }) => {
    const [localResources, setLocalResources] = useState(resources);
    const { save, isSaving } = useBusinessPostApi(`/integrations/other/zoom`);
    const { data: bookableResourceData, isLoading } =
        useBookableResourcesQuery();

    const bookableResources = bookableResourceData || [];

    useEffect(() => {
        setLocalResources(resources);
    }, [resources]);

    function submit(ev) {
        ev.preventDefault();

        save({
            resources: localResources,
        }).then(result => {
            setZoomQuery(result);
        });
    }
    // Show a dropdown for each bookable resource that lets them pick the zoom account
    //  the mapping is stored in resources

    if (isLoading) {
        return <Loading />;
    }

    if (bookableResources.length <= 1 && accounts.length <= 1) {
        return null;
    }

    return (
        <div className="mt-6">
            <div className="panel-label">Zoom Account Mapping</div>
            <form className="">
                <div className="divide-y panel pl-4  mb-4">
                    {bookableResources.map(resource => (
                        <div key={resource.id}>
                            <div className="flex justify-between items-center py-2 pr-4">
                                <div className="flex-1">{resource.name}</div>
                                <div className="flex-1 ml-2">
                                    <Select
                                        value={localResources[resource.id]}
                                        onChange={newValue =>
                                            setLocalResources({
                                                ...localResources,
                                                [resource.id]: newValue,
                                            })
                                        }
                                        options={accounts.map(account => ({
                                            value: account.id,
                                            label: `${account.display_name} (${account.email})`,
                                        }))}
                                        inputClassName="w-full sm:w-64"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <button className="btn-save" type="button" onClick={submit}>
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
            </form>
        </div>
    );
};

const ZoomIntegrationBlock = ({}) => {
    const { data, isLoading } = useZoomQuery();

    const isConnected = !isLoading && !!data?.connected;
    const accounts = data?.accounts || [];
    const errors = data?.errors || {};

    return (
        <IntegrationSettingsBlock
            title="Zoom"
            isLoading={isLoading}
            description="Generate Zoom meetings for events"
            icon={<ZoomIcon />}
            isConnected={isConnected}
            hideSave={true}
            error={data?.error}
            getSettingsContent={({ settings, setSettings }) => (
                <div>
                    <div className="mb-4">
                        <div className="mb-2">
                            Connect to Zoom to create Zoom meeting links for
                            bookings.
                        </div>

                        <div className="panel pl-4 divide-y mb-4">
                            {accounts.map(userinfo => (
                                <ZoomAccountRow
                                    userinfo={userinfo}
                                    key={userinfo.id}
                                    error={errors[userinfo.id]}
                                />
                            ))}
                        </div>

                        <a
                            href={data?.authUrl}
                            className={`${isConnected ? 'btn-light-pill' : 'btn-pill'} no-underline`}
                        >
                            {!isConnected ? 'Connect' : 'Add Another Account'}
                        </a>
                    </div>

                    {accounts.length > 1 && (
                        <ZoomResourceMapping
                            resources={data?.resources || []}
                            accounts={accounts}
                        />
                    )}

                    {isConnected && (
                        <div className="mt-2">
                            In{' '}
                            <Link
                                to={getBusinessUrl('/scheduling/event-types')}
                            >
                                Event Types
                            </Link>{' '}
                            edit a type and set the Location to Zoom to generate
                            a meeting link when that type is scheduled.
                        </div>
                    )}
                </div>
            )}
        />
    );
};

export default ZoomIntegrationBlock;
